import {
  getIBPulseFeed,
  recordIBPulseEngagement,
  setIBPulseDismissed,
  setIBPulseFilter,
  setIBPulseUpcomingCollapsed,
} from '@/api/ibModule';

// Sources a feed event can carry, in chip display order. 'placeholder' events
// (synthetic, from the placeholder feeder) are rendered but get no chip.
export const PULSE_SOURCES = ['news', 'usda_release', 'calendar', 'price_move', 'advisor_manual'];

// The server tracks dismissals per surface: the feed row (its default) and the
// upcoming-release announcement above it. Closing a card must not hide the
// release from the feed once it lands, so the card sends its own scope.
export const UPCOMING_SCOPE = 'upcoming';

function dedupeById(existing, incoming) {
  const seen = new Set(existing.map((event) => event.id));
  return (incoming || []).filter((event) => !seen.has(event.id));
}

// The loaded feed's bounds by time rather than by position. A release that lands
// is prepended so the advisor actually sees it, even though it was created back
// when it was announced — so position no longer implies age, and the live-poll
// and "show previous" cursors have to be computed from the timestamps.
function boundaryCreatedAt(events, keepLater) {
  return events.reduce((bound, event) => {
    const isLater = Date.parse(event.created_at) > Date.parse(bound);
    return isLater === keepLater ? event.created_at : bound;
  }, events[0].created_at);
}

const newestCreatedAt = (events) => boundaryCreatedAt(events, true);
const oldestCreatedAt = (events) => boundaryCreatedAt(events, false);

export default {
  namespaced: true,

  state: () => ({
    // Lazy-loaded, newest-first. Preloaded with today's events (or the latest
    // few) and grown backwards by "Show previous events"; new events prepend on
    // the live poll. Accumulation lives here in Vuex, so it survives tab
    // switches and resets only on a full page reload (fresh store).
    events: [],
    freshHasMore: false,
    catchUp: { events: [], hasMore: false },
    // The announcement strip above the feed: scheduled releases still ahead of
    // us plus any that landed in the last day, and the advisor's persisted
    // minimize choice. Server-owned and small, so every feed load replaces it
    // rather than accumulating — that is also how a card learns it has landed.
    upcoming: { events: [], collapsed: false },
    // Cards closed in this session (AC7.2 semantics, announcement scope): hidden
    // immediately here, then persisted per advisor.
    dismissedUpcomingIds: [],
    // Releases already moved into the feed when their card flipped to published.
    // Tracked so a card that stays on screen for its last day can't re-prepend
    // the same event on every poll — the server may be legitimately excluding it
    // from the feed slice (the advisor dismissed the row, or blasted it).
    promotedIds: [],
    loading: false,
    loaded: false,
    error: false,
    // Source segment chips: the sources currently suppressed (hidden). Empty
    // means "show everything" — an advisor toggles a category off temporarily
    // without dismissing individual items, and unknown sources (e.g. the
    // placeholder feeder) stay filterable because the check is an exclusion.
    suppressedSources: [],
    // Optimistic dismissals for this session: a dismissed event is hidden
    // immediately here, then persisted per-advisor server-side (AC7.2) so it
    // stays gone across reloads. The server also excludes dismissed events from
    // the default feed, so these ids only matter until the next fetch.
    dismissedIds: [],
    // Session-only "Show dismissed" toggle (AC7.4). When on, feed fetches ask
    // the server to keep this advisor's dismissed events in the fresh slice
    // (each flagged with `dismissed`), so they can be reviewed and restored.
    // Not persisted — it resets to hidden on reload.
    showDismissed: false,
    // Events blasted in this session, so a send moves the item into the
    // catch-up table immediately; the next poll reconciles with the server,
    // which owns the durable split (IBBroadcast.pulse_event).
    blastedIds: [],
    // Book filtering (Story 1.4). The feed is scoped server-side to the
    // commodities in the advisor's book (union of their active clients' crops)
    // unless showAll is on. showAll is persisted per advisor server-side; the
    // feed response echoes both, so this mirrors the server's durable state.
    // bookTags drives the "filtering to N commodities" hint and empty-state copy.
    showAll: false,
    bookTags: [],
  }),

  getters: {
    // The fresh feed: not chip-suppressed, not yet blasted, and — unless the
    // "Show dismissed" view is on — not dismissed (server-flagged or optimistic).
    freshEvents(state) {
      return state.events
        .filter((event) => state.showDismissed || !event.dismissed)
        .filter((event) => state.showDismissed || !state.dismissedIds.includes(event.id))
        .filter((event) => !state.suppressedSources.includes(event.source))
        .filter((event) => !state.blastedIds.includes(event.id));
    },

    // The catch-up table: events blasted this session (moved over locally,
    // newest send first) ahead of the server-provided rows.
    catchUpEvents(state) {
      const serverIds = new Set(state.catchUp.events.map((event) => event.id));
      const local = state.events.filter(
        (event) => state.blastedIds.includes(event.id) && !serverIds.has(event.id),
      );
      return [...local, ...state.catchUp.events];
    },

    isCatchUp: (state, getters) => (eventId) => (
      getters.catchUpEvents.some((event) => event.id === eventId)
    ),

    // The announcement cards on screen: server-visible ones minus anything
    // closed here, unless the "Show dismissed" view is on.
    upcomingEvents(state) {
      return state.upcoming.events
        .filter((event) => state.showDismissed || !event.dismissed)
        .filter((event) => state.showDismissed || !state.dismissedUpcomingIds.includes(event.id));
    },
  },

  mutations: {
    setLoading(state, value) {
      state.loading = Boolean(value);
    },
    setError(state, value) {
      state.error = Boolean(value);
    },
    // Server-owned book-filter state, echoed on every feed load and returned by
    // the toggle endpoint. Guarded so a slice response without a filters block
    // (older/newer head refreshes may omit it) doesn't clobber known state.
    setFilters(state, filters) {
      if (!filters) return;
      state.showAll = Boolean(filters.show_all);
      state.bookTags = filters.book || [];
    },
    // Initial load / Refresh: replace the fresh feed from the top.
    setFresh(state, { events, has_more: hasMore } = {}) {
      state.events = events || [];
      state.freshHasMore = Boolean(hasMore);
      state.loaded = true;
      state.error = false;
    },
    // "Show previous events": older batch appended at the tail.
    appendFresh(state, { events, has_more: hasMore } = {}) {
      state.events = [...state.events, ...dedupeById(state.events, events)];
      state.freshHasMore = Boolean(hasMore);
    },
    // Live poll: newer events prepended at the head (tail untouched).
    prependFresh(state, { events } = {}) {
      state.events = [...dedupeById(state.events, events), ...state.events];
    },
    setCatchUp(state, { events, has_more: hasMore } = {}) {
      state.catchUp = { events: events || [], hasMore: Boolean(hasMore) };
    },
    appendCatchUp(state, { events, has_more: hasMore } = {}) {
      state.catchUp = {
        events: [...state.catchUp.events, ...dedupeById(state.catchUp.events, events)],
        hasMore: Boolean(hasMore),
      };
    },
    prependCatchUp(state, { events } = {}) {
      state.catchUp = {
        ...state.catchUp,
        events: [...dedupeById(state.catchUp.events, events), ...state.catchUp.events],
      };
    },
    // Server-owned announcement strip, replaced on every feed load. A card that
    // has flipped to published is also pushed to the head of the feed: the
    // release was announced days ago, so its created_at sits behind the live
    // poll's cursor and the feed would otherwise never surface it.
    setUpcoming(state, { events, collapsed } = {}) {
      const cards = events || [];
      state.upcoming = { events: cards, collapsed: Boolean(collapsed) };
      const loaded = new Set(state.events.map((event) => event.id));
      const landed = cards.filter((card) => (
        card.published && !loaded.has(card.id) && !state.promotedIds.includes(card.id)
      ));
      if (!landed.length) return;
      state.promotedIds = [...state.promotedIds, ...landed.map((card) => card.id)];
      state.events = [...landed, ...state.events];
    },
    setUpcomingCollapsed(state, collapsed) {
      state.upcoming = { ...state.upcoming, collapsed: Boolean(collapsed) };
    },
    dismissUpcoming(state, eventId) {
      if (!state.dismissedUpcomingIds.includes(eventId)) {
        state.dismissedUpcomingIds = [...state.dismissedUpcomingIds, eventId];
      }
      state.upcoming = {
        ...state.upcoming,
        events: state.upcoming.events.map(
          (event) => (event.id === eventId ? { ...event, dismissed: true } : event),
        ),
      };
    },
    restoreUpcoming(state, eventId) {
      state.dismissedUpcomingIds = state.dismissedUpcomingIds.filter((id) => id !== eventId);
      state.upcoming = {
        ...state.upcoming,
        events: state.upcoming.events.map(
          (event) => (event.id === eventId ? { ...event, dismissed: false } : event),
        ),
      };
    },
    toggleSource(state, source) {
      if (state.suppressedSources.includes(source)) {
        state.suppressedSources = state.suppressedSources.filter((s) => s !== source);
      } else {
        state.suppressedSources = [...state.suppressedSources, source];
      }
    },
    clearSourceFilter(state) {
      state.suppressedSources = [];
    },
    dismissEvent(state, eventId) {
      if (!state.dismissedIds.includes(eventId)) {
        state.dismissedIds = [...state.dismissedIds, eventId];
      }
      // Flag the loaded row too, so it reads as dismissed (Restore affordance)
      // when it stays visible under the "Show dismissed" view.
      state.events = state.events.map(
        (event) => (event.id === eventId ? { ...event, dismissed: true } : event),
      );
    },
    restoreEvent(state, eventId) {
      state.dismissedIds = state.dismissedIds.filter((id) => id !== eventId);
      state.events = state.events.map(
        (event) => (event.id === eventId ? { ...event, dismissed: false } : event),
      );
    },
    setShowDismissed(state, value) {
      state.showDismissed = Boolean(value);
    },
    markBlasted(state, eventId) {
      if (!state.blastedIds.includes(eventId)) {
        state.blastedIds = [...state.blastedIds, eventId];
      }
    },
  },

  actions: {
    // Initial preload of both collections (first mount and the Refresh button —
    // Refresh collapses any expanded "Show previous" back to the top).
    async fetchInitial({ commit, state }) {
      commit('setLoading', true);
      try {
        const {
          fresh, catch_up: catchUp, filters, upcoming,
        } = await getIBPulseFeed({
          fresh_direction: 'initial',
          catch_up_direction: 'initial',
          ...(state.showDismissed ? { show_dismissed: true } : {}),
        });
        commit('setFilters', filters);
        commit('setFresh', fresh);
        commit('setCatchUp', catchUp);
        commit('setUpcoming', upcoming);
      } catch (error) {
        commit('setError', true);
      } finally {
        commit('setLoading', false);
      }
    },

    // Toggle the "show all commodities" book-filter override (AC4.2). The choice
    // is persisted server-side (AC4.3); because the feed is scoped server-side,
    // a re-fetch is required to widen/narrow the returned set. fetchInitial
    // collapses any expanded history back to the top, which is the right reset
    // when the filter changes.
    async setShowAll({ commit, dispatch }, showAll) {
      try {
        const { filters } = await setIBPulseFilter({ show_all: showAll });
        commit('setFilters', filters);
      } catch (error) {
        commit('setError', true);
        return;
      }
      await dispatch('fetchInitial');
    },

    async loadPreviousFresh({ commit, state }) {
      if (!state.events.length || state.loading) return;
      commit('setLoading', true);
      try {
        const { fresh } = await getIBPulseFeed({
          fresh_direction: 'older',
          fresh_cursor: oldestCreatedAt(state.events),
          ...(state.showDismissed ? { show_dismissed: true } : {}),
        });
        commit('appendFresh', fresh);
      } catch (error) {
        commit('setError', true);
      } finally {
        commit('setLoading', false);
      }
    },

    async loadPreviousCatchUp({ commit, state }) {
      if (!state.catchUp.events.length || state.loading) return;
      commit('setLoading', true);
      try {
        const cursor = state.catchUp.events[state.catchUp.events.length - 1].last_blasted_at;
        const { catch_up: catchUp } = await getIBPulseFeed({
          catch_up_direction: 'older',
          catch_up_cursor: cursor,
        });
        commit('appendCatchUp', catchUp);
      } catch (error) {
        commit('setError', true);
      } finally {
        commit('setLoading', false);
      }
    },

    // Live head refresh (30s poll): prepend events newer than what's loaded,
    // leaving accumulated "Show previous" history intact. A collection with
    // nothing loaded yet gets an initial load instead so it can cold-start.
    async refreshHead({ commit, state }) {
      const freshDirection = state.events.length ? 'newer' : 'initial';
      const catchDirection = state.catchUp.events.length ? 'newer' : 'initial';
      const payload = {
        fresh_direction: freshDirection,
        catch_up_direction: catchDirection,
        ...(state.showDismissed ? { show_dismissed: true } : {}),
      };
      if (freshDirection === 'newer') payload.fresh_cursor = newestCreatedAt(state.events);
      if (catchDirection === 'newer') {
        payload.catch_up_cursor = state.catchUp.events[0].last_blasted_at;
      }
      try {
        const {
          fresh, catch_up: catchUp, filters, upcoming,
        } = await getIBPulseFeed(payload);
        commit('setFilters', filters);
        if (fresh) commit(freshDirection === 'newer' ? 'prependFresh' : 'setFresh', fresh);
        if (catchUp) commit(catchDirection === 'newer' ? 'prependCatchUp' : 'setCatchUp', catchUp);
        if (upcoming) commit('setUpcoming', upcoming);
      } catch (error) {
        // A failed poll keeps whatever is already on screen.
      }
    },

    // Engagement counters are best-effort aggregates — the UI action (hide,
    // blast composed) must never be blocked or rolled back by a failed report.
    async recordEngagement(context, payload) {
      try {
        await recordIBPulseEngagement(payload);
      } catch (error) {
        // counters are lossy by design
      }
    },

    // Dismiss (AC7.1/7.2): hide immediately, then persist. The server owns the
    // durable per-advisor state and bumps the aggregate dismiss counter, so a
    // failed call just leaves the optimistic hide to reconcile on the next load.
    async dismissEvent({ commit }, eventId) {
      commit('dismissEvent', eventId);
      try {
        await setIBPulseDismissed({ event_id: eventId, dismissed: true });
      } catch (error) {
        // best-effort; the optimistic hide stands until a reload reconciles
      }
    },

    // Restore a dismissed event (AC7.4, "changed their mind").
    async restoreEvent({ commit }, eventId) {
      commit('restoreEvent', eventId);
      try {
        await setIBPulseDismissed({ event_id: eventId, dismissed: false });
      } catch (error) {
        // best-effort
      }
    },

    // Minimize / expand every announcement card at once. Applied optimistically
    // (the strip must react immediately) then persisted; the server's answer is
    // the truth, and a failed save just leaves the local state to reconcile on
    // the next load.
    async setUpcomingCollapsed({ commit }, collapsed) {
      commit('setUpcomingCollapsed', collapsed);
      try {
        const saved = await setIBPulseUpcomingCollapsed({ collapsed: Boolean(collapsed) });
        commit('setUpcomingCollapsed', saved.collapsed);
      } catch (error) {
        // best-effort; the optimistic state stands until a reload reconciles
      }
    },

    // Close one announcement card. Scoped to the announcement, so the release
    // itself still lands in the feed when it publishes.
    async dismissUpcomingEvent({ commit }, eventId) {
      commit('dismissUpcoming', eventId);
      try {
        await setIBPulseDismissed({
          event_id: eventId, dismissed: true, scope: UPCOMING_SCOPE,
        });
      } catch (error) {
        // best-effort; the optimistic hide stands until a reload reconciles
      }
    },

    // Bring a closed card back (the "Show dismissed" view, AC7.4).
    async restoreUpcomingEvent({ commit }, eventId) {
      commit('restoreUpcoming', eventId);
      try {
        await setIBPulseDismissed({
          event_id: eventId, dismissed: false, scope: UPCOMING_SCOPE,
        });
      } catch (error) {
        // best-effort
      }
    },

    // Flip the session-only "Show dismissed" view (AC7.4). The dismissed split
    // is resolved server-side, so re-fetch the feed with the new flag; like the
    // book-filter toggle, this collapses any expanded history back to the top.
    async setShowDismissed({ commit, dispatch }, showDismissed) {
      commit('setShowDismissed', Boolean(showDismissed));
      await dispatch('fetchInitial');
    },
  },
};
